import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Spinner from "sushiweb/Spinner/Spinner";
import Header from "../Header/Header";
import Footer from "../Footer/Footer";
import { FlexColumn, SushiModal, Button as PrimaryButton } from "../Common/styles.common";
import ShowToast from "../Common/Toast";
import useVendorStore from "../HomePage/useVendorStore";
import { LS_KEYS } from "utils/constants/localStorageKeys";
import styles from "./PackagingCompliance.module.scss";

const FSSAI_LENGTH = 14;
const ADDRESS_MAX_LENGTH = 400;
const FSSAI_STATUS = {
  FOUND: "id_found",
  NOT_FOUND: "id_not_found",
  SOURCE_DOWN: "source_down",
};

const getFssaiVerificationError = (data) => {
  const status = String(data?.status || "").toLowerCase();
  if (status === FSSAI_STATUS.SOURCE_DOWN) {
    return "FSSAI verification is temporarily unavailable. Please try again later.";
  }
  if (status === FSSAI_STATUS.NOT_FOUND) {
    return "FSSAI number not found. Please check and try again.";
  }
  if (status !== FSSAI_STATUS.FOUND) {
    return "Could not verify this FSSAI number. Please try again.";
  }
  if (String(data?.validity || "").toLowerCase() !== "active") {
    return "FSSAI licence is expired.";
  }
  return "";
};

const composeAddress = (details) => {
  const parts = [details?.address, details?.state, details?.pincode].filter(
    (p) => !!(p && String(p).trim())
  );
  return parts.join(", ");
};

const Field = ({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
  inputMode,
  multiline,
  error,
  disabled = false,
}) => {
  const filled = value !== "" && value !== null && value !== undefined;
  const textareaRef = useRef(null);

  // Grow the textarea to fit its content as the vendor types / wraps lines.
  useEffect(() => {
    if (multiline && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [multiline, value]);

  return (
    <div className={styles.fieldGroup}>
      {disabled ? (
        <span className={styles.disabledFieldLabel}>{label}</span>
      ) : null}
      <div
        className={`${styles.fieldBox} ${multiline ? styles.fieldBoxMultiline : ""} ${filled && !disabled ? styles.floating : ""} ${error ? styles.fieldBoxError : ""} ${disabled ? styles.fieldBoxDisabled : ""}`}
      >
        {filled && !disabled ? (
          <span className={styles.fieldLabel}>{label}</span>
        ) : null}
        {multiline ? (
          <textarea
            ref={textareaRef}
            className={`${styles.fieldInput} ${styles.fieldTextarea}`}
            rows={1}
            value={value}
            placeholder={placeholder || label}
            aria-invalid={!!error}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.value)}
          />
        ) : (
          <input
            className={styles.fieldInput}
            type={type}
            inputMode={inputMode}
            value={value}
            placeholder={placeholder || label}
            aria-invalid={!!error}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.value)}
          />
        )}
      </div>
      {error ? (
        <span className={`${styles.helperText} ${styles.helperTextError}`}>
          {error}
        </span>
      ) : null}
    </div>
  );
};

const PackagingCompliance = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const outletId = window.localStorage.getItem(LS_KEYS.OUTLET_ID);
  const sellerOutletName =
    window.localStorage.getItem(LS_KEYS.OUTLET_NAME) || "";

  const product = location?.state?.product || null;
  const vendorDetails = location?.state?.vendorDetails || {};
  const requirements = location?.state?.requirements || {};
  const returnTo = location?.state?.returnTo || "/download-labels";
  const hasComplianceState = !!(
    location?.state?.vendorDetails && location?.state?.requirements
  );

  const upsertPackagingLabelComplianceDetails = useVendorStore(
    (s) => s.upsertPackagingLabelComplianceDetails
  );
  const getFssaiDetails = useVendorStore(
    (s) => s.getFssaiDetails
  );

  const [fssaiNumber, setFssaiNumber] = useState(vendorDetails?.fssai_number || "");
  const [address, setAddress] = useState(vendorDetails?.vendor_address || "");
  const [careName, setCareName] = useState(vendorDetails?.consumer_care_name || "");
  const [carePhone, setCarePhone] = useState(vendorDetails?.consumer_care_phone || "");
  const [careEmail, setCareEmail] = useState(vendorDetails?.consumer_care_email || "");
  const [careAddress, setCareAddress] = useState(
    vendorDetails?.consumer_care_address || ""
  );

  const [fetchingFssai, setFetchingFssai] = useState(false);
  // Pre-filled FSSAI numbers come from an already-verified record, so they
  // start out verified; anything the vendor types must be verified explicitly.
  const [fssaiVerified, setFssaiVerified] = useState(
    !!vendorDetails?.fssai_number
  );
  const [fssaiError, setFssaiError] = useState("");
  const [tncAccepted, setTncAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  // The app disables document scroll globally (html, body { overflow: hidden }).
  // Re-enable it while this (taller-than-viewport) screen is mounted.
  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "auto";
    document.documentElement.style.overflow = "auto";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  // Editing the number invalidates any earlier verification — the vendor has to
  // hit Verify again before Submit unlocks.
  const handleFssaiChange = (raw) => {
    const value = raw.replace(/\D/g, "").slice(0, FSSAI_LENGTH);
    setFssaiNumber(value);
    setFssaiVerified(false);
    setFssaiError("");
  };

  const canVerifyFssai =
    fssaiNumber.length === FSSAI_LENGTH && !fssaiVerified && !fetchingFssai;

  const handleVerifyFssai = () => {
    if (!canVerifyFssai) return;
    setFetchingFssai(true);
    setFssaiError("");
    getFssaiDetails(
      { fssai_number: fssaiNumber },
      (data) => {
        setFetchingFssai(false);
        const verificationError = getFssaiVerificationError(data);
        if (verificationError) {
          setFssaiVerified(false);
          setFssaiError(verificationError);
          return;
        }
        setFssaiVerified(true);
        const composed = composeAddress(data);
        if (composed) setAddress(composed);
      },
      (errMsg) => {
        setFetchingFssai(false);
        setFssaiVerified(false);
        setFssaiError(errMsg || "Could not verify this FSSAI number.");
      }
    );
  };

  const isFssaiRequired = requirements?.fssai_logo === true;
  const hasFssaiNumber = !!fssaiNumber.trim();
  const fssaiOk = hasFssaiNumber
    ? fssaiNumber.length === FSSAI_LENGTH && fssaiVerified
    : !isFssaiRequired;
  const addressTooLong = address.length > ADDRESS_MAX_LENGTH;

  const isValid = useMemo(
    () =>
      hasComplianceState &&
      fssaiOk &&
      tncAccepted &&
      !!address.trim() &&
      !addressTooLong &&
      !!careName.trim() &&
      !!carePhone.trim() &&
      !!careEmail.trim() &&
      !!careAddress.trim(),
    [
      fssaiOk,
      hasComplianceState,
      tncAccepted,
      address,
      addressTooLong,
      careName,
      carePhone,
      careEmail,
      careAddress,
    ]
  );

  const handleSubmit = () => {
    if (!hasComplianceState || !isValid || submitting) return;
    setSubmitting(true);
    setError("");
    const vendorDetailsPayload = {
      vendor_address: address.trim(),
      consumer_care_name: careName.trim(),
      consumer_care_phone: carePhone.trim(),
      consumer_care_email: careEmail.trim(),
      consumer_care_address: careAddress.trim(),
    };
    if (fssaiNumber.trim()) {
      vendorDetailsPayload.fssai_number = fssaiNumber.trim();
    }
    upsertPackagingLabelComplianceDetails(
      {
        vendor_id: Number(outletId),
        vendor_details: vendorDetailsPayload,
        tnc_accepted: true,
      },
      () => {
        setSubmitting(false);
        setShowSuccess(true);
      },
      (errMsg) => {
        setSubmitting(false);
        setError(errMsg || "Failed to submit compliance details. Please try again.");
      }
    );
  };

  const handleSuccessOkay = () => {
    setShowSuccess(false);
    if (product) {
      navigate("/download-labels/download", {
        replace: true,
        state: { product, requirements, returnTo },
      });
    } else {
      navigate(returnTo, { replace: true });
    }
  };

  if (!hasComplianceState) {
    return <Navigate to="/download-labels" replace />;
  }

  return (
    <React.Fragment>
      <Header showLogout={false} showProfile={true} />
      <div className={styles.container}>
        <div className={styles.titleRow}>
          <button
            className={styles.backBtn}
            onClick={() => navigate(returnTo, { replace: true })}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5m0 0l6 6m-6-6l6-6" stroke="#1c1c1c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className={styles.pageTitle}>Packaging details</h1>
        </div>

        <div className={styles.card}>
          <span className={styles.cardTitle}>FSSAI details</span>
          <div className={styles.fieldGroup}>
            <div
              className={`${styles.fieldBox} ${fssaiNumber ? styles.floating : ""} ${
                fssaiError ? styles.fieldBoxError : ""
              }`}
            >
              {fssaiNumber ? (
                <span className={styles.fieldLabel}>FSSAI number</span>
              ) : null}
              <input
                className={styles.fieldInput}
                value={fssaiNumber}
                placeholder="Enter FSSAI number..."
                inputMode="numeric"
                onChange={(e) => handleFssaiChange(e.target.value)}
              />
              {fssaiVerified ? (
                <span className={styles.verifiedBadge}>Verified</span>
              ) : (
                <button
                  type="button"
                  className={`${styles.verifyBtn} ${
                    canVerifyFssai ? "" : styles.verifyBtnDisabled
                  }`}
                  onClick={handleVerifyFssai}
                  disabled={!canVerifyFssai}
                >
                  {fetchingFssai ? "Verifying..." : "Verify"}
                </button>
              )}
            </div>
            <span
              className={`${styles.helperText} ${fssaiError ? styles.helperTextError : ""}`}
            >
              {fssaiError ||
                (fssaiVerified
                  ? "FSSAI number verified."
                  : "Mandatory for food products only")}
            </span>
          </div>
        </div>

        <div className={styles.card}>
          <span className={styles.cardTitle}>Your details</span>
          <Field
            label="Supplier name"
            value={sellerOutletName}
            placeholder="Supplier name"
            disabled
          />
          <Field
            label="Complete address"
            value={address}
            onChange={setAddress}
            placeholder="Enter complete address"
            multiline
            error={
              addressTooLong ? "Address must be 400 characters or less" : ""
            }
          />
        </div>

        <div className={styles.card}>
          <span className={styles.cardTitle}>Customer care details</span>
          <Field
            label="Consumer care name"
            value={careName}
            onChange={setCareName}
            placeholder="Enter consumer care name"
          />
          <Field
            label="Consumer care number"
            value={carePhone}
            onChange={setCarePhone}
            placeholder="Enter consumer care number"
            inputMode="tel"
          />
          <Field
            label="Consumer care email"
            value={careEmail}
            onChange={setCareEmail}
            placeholder="Enter consumer care email"
            type="email"
          />
          <Field
            label="Consumer care address"
            value={careAddress}
            onChange={setCareAddress}
            placeholder="Enter consumer care address"
            multiline
          />
        </div>
      </div>

      <div className={styles.footerBar}>
        <label className={styles.tncRow}>
          <input
            type="checkbox"
            className={styles.tncCheckbox}
            checked={tncAccepted}
            onChange={(e) => setTncAccepted(e.target.checked)}
          />
          <span className={styles.tncText}>
            I agree to the{" "}
            <a
              className={styles.tncLink}
              href="/download-labels/tnc"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              Terms and Conditions
            </a>{" "}
            and take full responsibility of the information entered.
          </span>
        </label>
        <div className={styles.submitAction}>
          <PrimaryButton
            className={`${styles.submitBtn} ${!isValid || submitting ? styles.submitBtnDisabled : ""} text-white`}
            onClick={handleSubmit}
            disabled={!isValid || submitting}
          >
            {submitting ? <Spinner size="small" color="white" /> : "Submit"}
          </PrimaryButton>
        </div>
      </div>

      <Footer />

      {showSuccess && (
        <SushiModal visible={showSuccess} isCentered onClose={handleSuccessOkay}>
          <FlexColumn className={styles.successModal}>
            <div className={styles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                <circle cx="32" cy="32" r="32" fill="#e2f5e9" />
                <circle cx="32" cy="32" r="20" fill="#34b364" />
                <path d="M23 32.5l6 6 12-12" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <span className={styles.successTitle}>Compliance complete</span>
            <div className={styles.successDivider} />
            <span className={styles.successOkay} onClick={handleSuccessOkay}>
              Okay
            </span>
          </FlexColumn>
        </SushiModal>
      )}

      {error && (
        <ShowToast
          errorMsg={error}
          type="error"
          onClose={() => setError("")}
        />
      )}
    </React.Fragment>
  );
};

export default PackagingCompliance;
